<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import Close from '$lib/icons/close.svelte';
	import Mill from '$lib/logos/mill.svelte';
	import { identify } from '$lib/segment/client';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { SbImage } from '$lib/storyblok/types';
	import { getCouponValue } from '$lib/utils/couponCode';
	import log from '$lib/utils/log';
	import type { ISbStoryData } from '@storyblok/js';
	import type { ResultProps as CouponResultProps } from './quizCouponCodeResult.svelte';
	import type { ResultProps as SingleStepResultProps } from './quizSingleStepResult.svelte';
	import { pushToDataLayer } from '$lib/utils/gtm';
	import { hashEmail } from '$lib/utils/email';
	import { event } from '$lib/utils/tracking';

	interface Props {
		content: ISbStoryData<{
			quiz_identifier: string;
			steps: Array<ISbStoryData['content']>;
			submit_step: Array<ISbStoryData['content']>;
			result_step: Array<ISbStoryData<CouponResultProps | SingleStepResultProps>['content']>;
			desktop_image?: SbImage;
		}>['content'];
		onclose?: () => void;
	}

	let submitting = $state(false);
	let errorMessage = $state<string | undefined>();

	async function handleSubmit(submitEvent: SubmitEvent) {
		submitEvent.preventDefault();
		submitting = true;

		try {
			const form = submitEvent.target as HTMLFormElement;
			const formData = new FormData(form);

			const email = formData.get('email')?.toString() ?? '';
			const couponIdentifier = formData.get('couponIdentifier')?.toString() ?? '';
			const couponInfoText = formData.get('couponInfoText')?.toString() ?? '';

			const response = await fetch('/api/emailCapture', {
				method: 'POST',
				body: JSON.stringify({
					email,
					couponIdentifier,
					couponInfoText,
					source: 'quiz',
					pathname: window.location.pathname
				})
			});

			if (!response.ok) {
				const error = await response.json();

				throw new Error(error?.message ?? 'Something went wrong, sorry :(');
			}

			const result = await response.json();

			if (result.couponCode) {
				await checkoutSession.applyCoupon(result.couponCode);
			}

			currentIndex++;

			const answerTexts = Array.from(form.querySelectorAll('.answer-label')).map(
				(node) => (node as HTMLElement).innerText
			);

			resultIndex = parseInt(formData.get('answerIndex')?.toString() || '');

			const hashedEmail = await hashEmail(email);

			pushToDataLayer({
				order: {
					email,
					hashed_email: hashedEmail,
					couponCode: result.couponCode ?? ''
				}
			});

			await identify({ email });
			await event('Quiz Submitted', {
				quizIdentifier: content.quiz_identifier,
				steps: [
					{
						selectedAnswer: answerTexts[resultIndex]
					}
				],
				result: content.result_step[0].results[resultIndex].tracking_label,
				discountCode: result.couponCode,
				discountType: 'amount',
				discountValue:
					result.couponValue ??
					(checkoutSession.data.product?.promotionCode?.coupon
						? getCouponValue(checkoutSession.data.product.promotionCode.coupon)
						: 0),
				email,
				emailSource: 'quiz',
				offer: couponInfoText,
				referringPage: window.location.pathname
			});
		} catch (error) {
			if (error instanceof Error) {
				errorMessage = error.message;
			} else {
				errorMessage = 'Something went wrong, sorry :(';
				log.scope('Single Step Quiz').error('Could not submit form', { error });
			}
		} finally {
			submitting = false;
		}
	}

	let { content, onclose }: Props = $props();

	let currentIndex = $state(0);
	let resultIndex: undefined | number = $state(undefined);

	let backButtonDisabled = $derived(currentIndex === 0 || currentIndex > content.steps.length);
</script>

<form
	onsubmit={handleSubmit}
	action="#"
	method="POST"
	class="relative h-full text-brown-1"
	use:editable={content}
>
	<input type="hidden" name="quizIdentifier" value={content.quiz_identifier} />

	<div
		id="site-header"
		class="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-1 pb-2 pt-3 max-lg:border-b max-lg:border-black/[0.08] max-lg:bg-white lg:items-start lg:pl-9 lg:pr-4 lg:pt-4"
	>
		<Button
			disabled={backButtonDisabled}
			onclick={() => currentIndex--}
			class={[
				'btn-white transition-transform lg:hidden',
				backButtonDisabled && '-translate-x-[110%]'
			]}><ArrowLeft class="h-6 w-6" /></Button
		>
		<a href="/" title="Home" class="lg:mt-3"
			><Mill class="h-6 text-green-3 lg:h-10 lg:text-white" /></a
		>
		<Button onclick={() => onclose?.()} class="btn-white"><Close class="h-6 w-6" /></Button>
	</div>

	<div class="h-full max-lg:pt-5 lg:flex">
		{#if content.desktop_image?.filename}
			<Image
				src={content.desktop_image.filename}
				wrapperClass="max-lg:hidden flex-1 h-full"
				class="h-full w-full object-cover"
			/>
		{/if}
		<div class="flex h-full flex-1 justify-center lg:items-center">
			<div class="mx-5 max-w-[432px]">
				{#each content.steps as block, index}
					<Blok content={block} oncontinue={() => currentIndex++} active={currentIndex === index} />
				{/each}
				<Blok
					content={content.submit_step[0]}
					active={currentIndex === content.steps.length}
					{errorMessage}
					{submitting}
					onclearerror={() => (errorMessage = undefined)}
				/>

				<div class={{ hidden: resultIndex === undefined }}>
					{#each content.result_step as block}
						<Blok {resultIndex} content={block} {onclose} />
					{/each}
				</div>
			</div>
		</div>
	</div>
</form>

<style lang="scss">
	@media (max-width: 1023px) {
		form {
			padding-top: var(--header-height);
		}
	}
</style>
